import * as React from "react"
import * as RechartsPrimitive from "recharts"

import { cn } from "../../lib/utils"

const ChartContainer = React.forwardRef(({ config, children, className, ...props }, ref) => {
    const chartId = React.useId()

    return (
        <div
            ref={ref}
            className={cn(
                "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-muted-foreground/40 [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-muted-foreground/40 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-muted-foreground [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-muted-foreground/40 [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted-foreground/20 [&_.recharts-reference-line_[stroke='#ccc']]:stroke-muted-foreground/40 [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
                className
            )}
            {...props}
        >
            <style
                dangerouslySetInnerHTML={{
                    __html: `
            [data-chart="${chartId}"] {
              ${Object.entries(config)
                            .map(([key, value]) => `--color-${key}: ${value.color};`)
                            .join("\n")}
            }
          `,
                }}
            />
            <RechartsPrimitive.ResponsiveContainer width="100%" height="100%" data-chart={chartId}>
                {children}
            </RechartsPrimitive.ResponsiveContainer>
        </div>
    )
})
ChartContainer.displayName = "Chart"

const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef(
    (
        {
            active,
            payload,
            className,
            indicator = "dot",
            hideLabel = false,
            hideIndicator = false,
            label,
            labelFormatter,
            labelClassName,
            formatter,
            color,
            nameKey,
            labelKey,
        },
        ref
    ) => {
        if (!active || !payload?.length) {
            return null
        }

        return (
            <div
                ref={ref}
                className={cn(
                    "grid min-w-[8rem] items-start gap-1.5 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-xl",
                    className
                )}
            >
                {!hideLabel && (
                    <div className={cn("font-medium", labelClassName)}>
                        {labelFormatter ? labelFormatter(label, payload) : label}
                    </div>
                )}
                <div className="grid gap-1.5">
                    {payload.map((item, index) => {
                        const key = `${nameKey || item.name || item.dataKey || "value"}`
                        const itemConfig = payload[0]?.payload?._chartConfig?.[key] || {}

                        return (
                            <div
                                key={item.dataKey}
                                className={cn(
                                    "flex w-full items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                                    indicator === "dot" && "items-center"
                                )}
                            >
                                {!hideIndicator && (
                                    <div
                                        className="shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]"
                                        style={{
                                            "--color-bg": item.color || item.payload.fill,
                                            "--color-border": item.color || item.payload.fill,
                                            width: "8px",
                                            height: "8px"
                                        }}
                                    />
                                )}
                                <div className="flex flex-1 justify-between leading-none">
                                    <div className="grid gap-1.5">
                                        <span className="text-muted-foreground">
                                            {itemConfig.label || item.name}
                                        </span>
                                    </div>
                                    {item.value && (
                                        <span className="font-mono font-medium tabular-nums text-foreground">
                                            {formatter ? formatter(item.value, item.name, item, index, payload) : item.value.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }
)
ChartTooltipContent.displayName = "ChartTooltip"

const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = React.forwardRef(
    ({ className, hideIcon = false, payload, verticalAlign = "bottom", nameKey }, ref) => {
        if (!payload?.length) {
            return null
        }

        return (
            <div
                ref={ref}
                className={cn(
                    "flex items-center justify-center gap-4",
                    verticalAlign === "top" ? "pb-3" : "pt-3",
                    className
                )}
            >
                {payload.map((item) => {
                    const key = `${nameKey || item.dataKey || "value"}`

                    return (
                        <div
                            key={item.value}
                            className={cn(
                                "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
                            )}
                        >
                            {!hideIcon && (
                                <div
                                    className="h-2 w-2 shrink-0 rounded-[2px]"
                                    style={{
                                        backgroundColor: item.color,
                                    }}
                                />
                            )}
                            {item.value}
                        </div>
                    )
                })}
            </div>
        )
    }
)
ChartLegendContent.displayName = "ChartLegend"

export {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
}
